import { getMessageTimestampMs } from './normalizeMessageTimestamp';

const BUTTON_KEYS = ['buttons', 'button', 'actions', 'action_buttons'];
const REPLY_TYPES = new Set(['button_reply', 'list_reply', 'quick_reply']);

const TYPE_ALIASES = {
  TEXT: 'text',
  BUTTONS: 'buttons',
  LIST: 'list',
  TEMPLATE: 'template',
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  FILE: 'document',
  FLOW: 'flow',
  ORDER: 'order',
  CATALOG: 'catalog',
  CTAURL: 'ctaurl',
  OPENAI: 'openai',
  INTERVENTION: 'intervention',
  ORDERSTATUS: 'orderstatus',
  ORDERDETAILS: 'order',
  order_details: 'order',
  catalog_message: 'catalog',
  catelog: 'catalog',
  LOCATION: 'location',
  PAYMENT: 'payment',
  NFM_REPLY: 'nfm_reply',
  ADDRESS: 'address',
};

function get(obj, path, fallback = undefined) {
  if (!obj) return fallback;
  const value = path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
  return value === undefined ? fallback : value;
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
}

function compact(values) {
  return values.filter((value) => value !== undefined && value !== null && value !== '');
}

function firstText(...values) {
  return compact(values).find((value) => typeof value === 'string');
}

function asText(block) {
  if (typeof block === 'string') return block;
  if (block && typeof block === 'object') return block.text || '';
  return '';
}

function normalizeRawType(rawType) {
  if (!rawType || typeof rawType !== 'string') return null;
  return TYPE_ALIASES[rawType] || TYPE_ALIASES[rawType.toUpperCase()] || rawType.toLowerCase();
}

function normalizeStatus(message) {
  return firstText(message?.status?.type, message?.status, message?.message_status) || null;
}

function normalizeAttribution(message, isOutbound) {
  const rawOrigin = `${message?.origin || ''}`.toUpperCase();
  if (rawOrigin === 'INBOUND' || isOutbound === false) return null;

  const chatType = typeof message?.chat_type === 'string' ? message.chat_type.trim() : '';

  if (chatType === 'bot') return 'Message by: Bot';
  if (chatType === 'human_agent' && message?.intervened_by) {
    return `Message by: ${message.intervened_by}`;
  }
  if (chatType) return `Message by: ${chatType}`;
  if (message?.intervened_by) return `Message by: ${message.intervened_by}`;
  return null;
}

function extractMessageText(message) {
  const content = message?.content;
  return firstText(
    get(content, 'body.text'),
    typeof get(content, 'body') === 'string' ? get(content, 'body') : null,
    typeof content === 'string' ? content : null,
    get(content, 'text'),
    typeof message?.body === 'string' ? message.body : null,
    get(message, 'body.text'),
    message?.text,
    typeof message?.message === 'string' ? message.message : null,
    get(message, 'interactive.body.text'),
    get(message, 'template.body.text')
  );
}

function extractFooter(message) {
  return firstText(
    get(message, 'content.footer.text'),
    get(message, 'content.footer'),
    get(message, 'interactive.footer.text'),
    get(message, 'template.footer.text'),
    message?.footer
  );
}

function extractHeader(message) {
  const header = message?.content?.header || message?.header;
  if (header && typeof header === 'object') {
    const headerType = (header.type || header.format || 'TEXT').toUpperCase();
    switch (headerType) {
      case 'IMAGE':
      case 'PHOTO':
        return {
          kind: 'image',
          url: firstText(header.link, header.url, header.image?.link, header.image?.url),
          text: null,
          fileName: null,
        };
      case 'VIDEO':
        return {
          kind: 'video',
          url: firstText(header.link, header.url, header.video?.link, header.video?.url),
          text: null,
          fileName: null,
        };
      case 'DOCUMENT':
      case 'FILE':
      case 'PDF':
        return {
          kind: 'document',
          url: firstText(header.link, header.url, header.document?.link, header.document?.url, header.file?.link, header.file?.url),
          text: null,
          fileName:
            firstText(
              header.filename,
              header.file_name,
              header.caption,
              header.name,
              header.document?.filename,
              header.document?.name,
              header.file?.filename
            ) || 'Document',
        };
      case 'TEXT':
        return { kind: 'text', text: header.text || '', url: null, fileName: null };
      default:
        if (header.text) return { kind: 'text', text: header.text, url: null, fileName: null };
        if (header.link || header.url) {
          return {
            kind: 'document',
            url: firstText(header.link, header.url),
            text: null,
            fileName: firstText(header.filename, header.file_name, header.caption, header.name) || 'Document',
          };
        }
        break;
    }
  }

  const textHeader = firstText(
    typeof header === 'string' ? header : null,
    get(message, 'interactive.header.text'),
    get(message, 'template.header.text'),
    message?.title
  );

  if (textHeader) {
    return { kind: 'text', text: textHeader, url: null, fileName: null };
  }

  return null;
}

function extractButtonLike(entry) {
  if (!entry) return null;
  const label = firstText(
    entry?.label,
    entry?.text,
    entry?.title,
    entry?.reply?.title,
    entry?.button_text,
    entry?.name
  );
  if (!label) return null;

  return {
    id: firstText(entry?.id, entry?.reply?.id, entry?.payload, entry?.value),
    label,
    type: firstText(entry?.type, entry?.reply?.type, entry?.sub_type),
    url: firstText(entry?.url, entry?.href, entry?.value),
  };
}

function extractButtons(message) {
  for (const key of BUTTON_KEYS) {
    const sources = [
      message?.[key],
      message?.content?.[key],
      message?.interactive?.[key],
      message?.body?.[key],
    ];

    for (const source of sources) {
      const buttons = toArray(source).map(extractButtonLike).filter(Boolean);
      if (buttons.length) return buttons;
    }
  }

  const componentButtons = toArray(message?.template?.components)
    .filter((component) => component?.type === 'BUTTONS')
    .flatMap((component) => toArray(component?.buttons).map(extractButtonLike).filter(Boolean));
  if (componentButtons.length) return componentButtons;

  const contentComponentButtons = toArray(message?.content?.components)
    .filter((component) => component?.type === 'BUTTONS')
    .flatMap((component) => toArray(component?.buttons).map(extractButtonLike).filter(Boolean));
  if (contentComponentButtons.length) return contentComponentButtons;

  const interactiveActionButtons = toArray(message?.interactive?.action?.buttons)
    .map(extractButtonLike)
    .filter(Boolean);
  if (interactiveActionButtons.length) return interactiveActionButtons;

  const templateButtons = toArray(message?.content?.buttons).map(extractButtonLike).filter(Boolean);
  if (templateButtons.length) return templateButtons;

  return [];
}

const INTERNAL_LIST_PAYLOAD_KEYS = new Set([
  'bot_id',
  'level',
  'flow_id',
  'node',
  'entity_id',
  'result_key',
  'result_name',
]);

function parseJsonObject(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function isInternalListPayload(value) {
  const parsed = parseJsonObject(value);
  if (!parsed || Array.isArray(parsed)) return false;
  return Object.keys(parsed).some((key) => INTERNAL_LIST_PAYLOAD_KEYS.has(key));
}

function pickListDisplayText(...values) {
  for (const value of compact(values)) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed || isInternalListPayload(trimmed)) continue;
    return trimmed;
  }
  return '';
}

function normalizeListOption(rawOption = {}) {
  const title = pickListDisplayText(rawOption?.title, rawOption?.label, rawOption?.name, rawOption?.text);
  const description = pickListDisplayText(
    rawOption?.description,
    rawOption?.subtitle,
    rawOption?.postbackText,
    rawOption?.body
  );

  return {
    id: firstText(rawOption?.id, rawOption?.value, rawOption?.payload) || null,
    title,
    ...(description ? { description } : {}),
  };
}

function extractListItems(message) {
  const items = toArray(message?.content?.list || message?.content?.items);
  if (items.length) {
    const listTitle = firstText(items[0]?.title, items[0]?.name) || 'Options';
    const options = items
      .flatMap((item) => toArray(item?.options).map(normalizeListOption))
      .filter((option) => option.title);

    if (options.length) {
      return { listTitle, options };
    }
  }

  const sectionSources = [
    message?.interactive?.action?.sections,
    message?.content?.sections,
    message?.content?.rows,
  ];

  for (const sections of sectionSources) {
    const normalizedSections = toArray(sections)
      .flatMap((section) =>
        toArray(section?.rows || section?.items || section?.options).map(normalizeListOption)
      )
      .filter((row) => row.title);

    if (normalizedSections.length) {
      return {
        listTitle: firstText(toArray(sections)[0]?.title) || 'Options',
        options: normalizedSections,
      };
    }
  }

  return { listTitle: 'Options', options: [] };
}

function stringifyTemplateParam(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value.text) return String(value.text);
  if (value.value) return String(value.value);
  return JSON.stringify(value);
}

function flattenTemplateParams(parameters) {
  if (!parameters) return [];
  if (Array.isArray(parameters)) return parameters.map(stringifyTemplateParam);
  const values = [];
  Object.values(parameters).forEach((entry) => {
    if (Array.isArray(entry)) entry.forEach((item) => values.push(stringifyTemplateParam(item)));
    else values.push(stringifyTemplateParam(entry));
  });
  return values;
}

function fillTemplatePlaceholders(text, parameters) {
  if (!text) return '';
  const values = flattenTemplateParams(parameters);
  return text.replace(/{{\s*(\d+)\s*}}/g, (_match, indexStr) => {
    const index = parseInt(indexStr, 10) - 1;
    const replacement = values[index];
    return replacement?.length ? replacement : `{{${indexStr}}}`;
  });
}

function extractTemplate(message) {
  const content = message?.content || {};
  const bodyBlock = content.body;
  let bodyText = '';
  let parameters = [];

  if (typeof bodyBlock === 'string') {
    bodyText = bodyBlock;
  } else if (bodyBlock) {
    bodyText = bodyBlock.text || '';
    parameters = bodyBlock.parameters;
  }

  const headerText = asText(content.header);
  const footerText = asText(content.footer);
  const resolvedBody = fillTemplatePlaceholders(bodyText, parameters);

  return {
    name: firstText(content.template_name, content.name) || null,
    header: headerText || null,
    body: resolvedBody || bodyText || null,
    footer: footerText || null,
    buttons: extractButtons(message),
  };
}

function extractMedia(message, kind) {
  const content = message?.content || {};
  const url = firstText(
    get(content, 'media.s3_object.url'),
    get(content, 'media.url'),
    content.previewUrl,
    content.url,
    message?.url,
    get(message, 'document.url'),
    get(message, 'image.url'),
    get(message, 'video.url'),
    get(message, 'audio.url')
  );

  const fileName = firstText(
    content.filename,
    content.name,
    get(message, 'document.filename'),
    get(message, 'document.name')
  );

  const caption = firstText(get(content, 'body.text'), content.text);

  if (!url && !fileName && !caption) return null;

  return {
    kind,
    url: url || null,
    fileName: fileName || null,
    caption: caption || null,
  };
}

function extractReply(message, rawType) {
  const content = message?.content || {};
  const replyPayload =
    content.button_reply ||
    content.list_reply ||
    content.quick_reply ||
    content.text ||
    null;

  let text = '';
  if (typeof replyPayload === 'string') {
    text = replyPayload;
  } else if (replyPayload && typeof replyPayload === 'object') {
    text = firstText(
      replyPayload.title,
      replyPayload.text,
      replyPayload.description,
      replyPayload.id
    );
    if (!text) text = JSON.stringify(replyPayload);
  }

  return {
    kind: rawType || 'reply',
    text: text || extractMessageText(message) || 'Reply',
  };
}

function detectKind(message, rawType, listItems, buttons) {
  const type = normalizeRawType(rawType);
  const lowerRaw = typeof rawType === 'string' ? rawType.toLowerCase() : '';

  if (type && REPLY_TYPES.has(type)) return 'reply';
  if (type === 'order' || lowerRaw === 'order_details') return 'order';
  if (type === 'catalog' || lowerRaw === 'catalog_message' || lowerRaw === 'catelog') return 'catalog';
  if (type === 'location') return 'location';
  if (type === 'payment') return 'payment';
  if (type === 'flow') return 'flow';
  if (type === 'nfm_reply') return 'nfm_reply';
  if (type === 'ctaurl') return 'ctaurl';
  if (type === 'openai') return 'openai';
  if (type === 'intervention') return 'intervention';
  if (type === 'orderstatus') return 'orderstatus';
  if (type === 'address') return 'address';
  if (type === 'template') return 'template';
  if (type === 'buttons') return 'buttons';
  if (type === 'list') return 'list';
  if (type === 'image' || type === 'video' || type === 'audio') return type;
  if (type === 'document' || type === 'file') return 'document';

  if (listItems.options.length) return 'list';
  if (buttons.length && (type === 'buttons' || rawType === 'BUTTONS')) return 'buttons';

  const mediaMime = firstText(message?.content?.mime_type, message?.mime_type, message?.file_type);
  if (mediaMime?.startsWith('image/')) return 'image';
  if (mediaMime?.startsWith('audio/')) return 'audio';
  if (mediaMime?.startsWith('video/')) return 'video';
  if (mediaMime?.includes('pdf') || mediaMime?.includes('document')) return 'document';

  if (message?.template || message?.content?.template || type === 'template') return 'template';
  if (buttons.length) return 'buttons';

  return 'text';
}

function formatFieldLabel(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function extractStructuredBody(message) {
  const content = message?.content || {};
  return firstText(
    get(content, 'body.text'),
    typeof content.body === 'string' ? content.body : null,
    get(content, 'body.system_message'),
    content.text,
    extractMessageText(message)
  );
}

function extractStructuredHeader(message) {
  const content = message?.content || {};
  return firstText(
    get(content, 'header.text'),
    typeof content.header === 'string' ? content.header : null
  );
}

function extractStructuredFooter(message) {
  const content = message?.content || {};
  return firstText(
    get(content, 'footer.text'),
    typeof content.footer === 'string' ? content.footer : null,
    extractFooter(message)
  );
}

function extractOrderItems(message) {
  const items = toArray(message?.content?.product_items);
  return items.map((item) => ({
    primary: firstText(item?.product_retailer_id, item?.name, 'Product') || 'Product',
    secondary: compact([
      item?.quantity != null ? `Qty: ${item.quantity}` : null,
      item?.item_price != null ? `Price: ${item.currency || 'INR'} ${item.item_price}` : null,
    ]).join(' · '),
    price: Number(item?.item_price || 0) * Number(item?.quantity || 0),
  }));
}

function extractFieldMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.entries(value).map(([key, val]) => ({
    label: formatFieldLabel(key),
    value: typeof val === 'object' ? JSON.stringify(val) : String(val),
  }));
}

function extractCard(message, kind) {
  const content = message?.content || {};
  const header = extractStructuredHeader(message);
  const body = extractStructuredBody(message);
  const footer = extractStructuredFooter(message);
  const buttons = extractButtons(message);

  const base = {
    badge: null,
    header,
    body,
    footer,
    items: [],
    fields: [],
    productId: null,
    location: null,
    linkButtons: [],
    readOnlyButtons: buttons.map((btn) => btn.label).filter(Boolean),
  };

  switch (kind) {
    case 'order': {
      const orderItems = extractOrderItems(message);
      const total = orderItems.reduce((sum, item) => sum + (item.price || 0), 0);
      return {
        ...base,
        badge: 'Order',
        items: orderItems.map(({ primary, secondary }) => ({ primary, secondary })),
        fields: total > 0 ? [{ label: 'Total Amount', value: `INR ${total}` }] : [],
      };
    }
    case 'catalog':
      return {
        ...base,
        badge: 'Catalog',
        productId: firstText(content?.action?.thumbnail_product_retailer_id),
      };
    case 'location': {
      const location = content?.location || content;
      const latitude = location?.latitude ?? location?.lat;
      const longitude = location?.longitude ?? location?.lng ?? location?.long;
      const url =
        latitude != null && longitude != null
          ? `https://www.google.com/maps?q=${latitude},${longitude}`
          : null;
      return {
        ...base,
        badge: 'Location',
        body: body || (latitude != null && longitude != null ? `Location: ${latitude}, ${longitude}` : 'Location shared'),
        location: latitude != null && longitude != null ? { latitude, longitude, url } : null,
      };
    }
    case 'payment':
      return {
        ...base,
        badge: 'Payment',
        fields: extractFieldMap(content?.text),
      };
    case 'nfm_reply': {
      const nfmValues = content?.values?.values || content?.values || {};
      const fields = extractFieldMap(nfmValues);
      return {
        ...base,
        badge: 'Form Reply',
        fields,
        body: fields.length ? null : body || content?.body || 'Form submitted',
      };
    }
    case 'ctaurl':
      return {
        ...base,
        badge: 'Link',
        linkButtons: buttons
          .filter((btn) => btn.url)
          .map((btn) => ({ label: btn.label, url: btn.url })),
      };
    case 'openai':
      return { ...base, badge: 'AI Response' };
    case 'intervention':
      return { ...base, badge: 'Intervention' };
    case 'orderstatus':
      return { ...base, badge: 'Order Status' };
    case 'address':
      return { ...base, badge: 'Address' };
    case 'flow':
      return { ...base, badge: 'Flow' };
    default:
      return null;
  }
}

const CARD_KINDS = new Set([
  'order',
  'catalog',
  'location',
  'payment',
  'flow',
  'nfm_reply',
  'ctaurl',
  'openai',
  'intervention',
  'orderstatus',
  'address',
]);

/**
 * Converts a raw intervene API message into a stable view-model for UI rendering.
 */
export function normalizeInterveneMessage(rawMessage) {
  const message =
    rawMessage?.message && typeof rawMessage.message === 'object'
      ? { ...rawMessage.message, ...rawMessage }
      : rawMessage?.message_data && typeof rawMessage.message_data === 'object'
        ? { ...rawMessage.message_data, ...rawMessage }
        : rawMessage;

  const rawType = firstText(
    message?.content?.type,
    message?.type,
    message?.message_type,
    message?.interactive?.type
  );

  const buttons = extractButtons(message);
  const listItems = extractListItems(message);
  const kind = detectKind(message, rawType, listItems, buttons);
  const header = extractHeader(message);
  const footer = extractFooter(message);
  const text = extractMessageText(message);
  const template = kind === 'template' ? extractTemplate(message) : null;
  const media =
    kind === 'image' || kind === 'video' || kind === 'audio' || kind === 'document'
      ? extractMedia(message, kind)
      : null;
  const reply = kind === 'reply' ? extractReply(message, normalizeRawType(rawType)) : null;
  const card = CARD_KINDS.has(kind) ? extractCard(message, kind) : null;

  const rawOrigin = `${message?.origin || ''}`.toUpperCase();
  const isExplicitInbound = rawOrigin === 'INBOUND';
  const isExplicitOutbound = rawOrigin === 'OUTBOUND';

  const isOutbound =
    isExplicitOutbound ||
    (!isExplicitInbound &&
      (message?.direction === 'outbound' ||
        message?.chat_type === 'human_agent' ||
        Boolean(message?.intervened_by)));

  return {
    id:
      firstText(message?.id, message?.msg_id, message?.message_id, message?.w_id) ||
      `${getMessageTimestampMs(message) || Date.now()}`,
    kind,
    rawType: rawType || null,
    direction: isOutbound ? 'outbound' : 'inbound',
    isMine: isOutbound,
    timestamp: getMessageTimestampMs(message),
    status: normalizeStatus(message),
    attribution: normalizeAttribution(message, isOutbound),
    senderName: firstText(message?.sender_name, message?.username, message?.intervened_by) || null,
    text,
    header,
    footer,
    buttons,
    listTitle: listItems.listTitle,
    listOptions: listItems.options,
    media,
    template,
    reply,
    card,
  };
}

// Backward-compatible alias
export const normalizeMessage = normalizeInterveneMessage;
